import {
  Badge,
  Box,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Tooltip,
  useDisclosure,
} from "@chakra-ui/react";
import { tagButtonStyle } from "@/components/FinancialShared/tableStyles";
import ConfirmDialog from "../components/ConfirmDialog";
import { boletoEstaPago, IBoletoDetalhadoRetorno } from "@/models/consignado/proposal-billings.model";
import { formatDateWithNoHours } from "@/utils/formatDateWithNoHours";

// A BMP devolve o mesmo boleto aninhado em cada parcela que ele cobre, então
// ele aparece repetido linha a linha na tabela. Resumir a faixa aqui é o que
// diz ao operador que é UMA cobrança, não vinte.
const formatarParcelasCobertas = (parcelas: number[]) => {
  const faixas: string[] = [];
  let inicio = parcelas[0];
  let anterior = parcelas[0];

  for (const numero of parcelas.slice(1)) {
    if (numero !== anterior + 1) {
      faixas.push(inicio === anterior ? `${inicio}` : `${inicio} a ${anterior}`);
      inicio = numero;
    }
    anterior = numero;
  }
  faixas.push(inicio === anterior ? `${inicio}` : `${inicio} a ${anterior}`);

  return faixas.join(", ");
};

type Props = {
  boleto: IBoletoDetalhadoRetorno | null;
  isOpen: boolean;
  onClose: () => void;
  // Quem chama decide quando fechar (após o cancelamento resolver).
  onCancelar: (boleto: IBoletoDetalhadoRetorno) => void | Promise<void>;
  isCancelando?: boolean;
};

// Modal "Gerenciar Boleto": download + cancelar.
//  - Download: abre `urlImpressao` (link de impressão da BMP, TAR-046).
//  - Cancelar: REAL (TAR-045) — DELETE /proposta/cobrancas/:ccb/boletos/
//    :codigoLiquidacao, com `codigoCobranca`. É irreversível na BMP, então
//    passa por confirmação. 409 se já cancelado ou pago — por isso pago e
//    cancelado nem mostram as ações.
export default function GerenciarBoletoModal({
  boleto,
  isOpen,
  onClose,
  onCancelar,
  isCancelando = false,
}: Props) {
  const confirmacao = useDisclosure();
  const pago = boleto ? boletoEstaPago(boleto) : false;
  const cancelado = boleto?.cancelado ?? false;
  const encerrado = pago || cancelado;

  const handleConfirmarCancelamento = async () => {
    if (!boleto) return;
    await onCancelar(boleto);
    confirmacao.onClose();
  };

  const handleDownload = () => {
    if (boleto?.urlImpressao) window.open(boleto.urlImpressao, "_blank", "noopener");
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay backdropFilter="blur(4px)" />
      <ModalContent borderRadius="lg" boxShadow="2xl">
        <ModalHeader
          fontFamily="Lexend Deca"
          fontSize="xl"
          fontWeight="600"
          borderBottomWidth="1px"
          borderBottomColor="gray.200"
          pb={4}
        >
          Gerenciar Boleto
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pt={6}>
          <Flex direction="column" gap={4}>
            <Flex bg="blue.50" borderRadius="md" p={4} borderLeftWidth="4px" borderLeftColor="blue.500">
              <Box fontFamily="Lexend Deca" fontSize="sm">
                <Flex align="center" gap={2} mb={1}>
                  <Text color="gray.600" fontSize="xs">Número do boleto</Text>
                  {cancelado && (
                    <Badge colorScheme="gray" borderRadius="md" fontSize="9px">Cancelado</Badge>
                  )}
                  {!cancelado && pago && (
                    <Badge colorScheme="green" borderRadius="md" fontSize="9px">Pago</Badge>
                  )}
                </Flex>
                <Text fontWeight="600" fontSize="md">
                  {boleto?.numeroBoleto ?? "—"}
                </Text>
                <Text color="gray.600" fontSize="xs" mt={2}>Código da cobrança</Text>
                <Text fontSize="sm" wordBreak="break-all">
                  {boleto?.codigoCobranca || "—"}
                </Text>
                {boleto?.parcelas_cobertas && boleto.parcelas_cobertas.length > 1 && (
                  <>
                    <Text color="gray.600" fontSize="xs" mt={2}>Parcelas cobertas</Text>
                    <Text fontSize="sm">
                      {formatarParcelasCobertas(boleto.parcelas_cobertas)}
                    </Text>
                  </>
                )}
                {boleto?.dtExpiracao && (
                  <>
                    <Text color="gray.600" fontSize="xs" mt={2}>Vencimento</Text>
                    <Text fontSize="sm">{formatDateWithNoHours(boleto.dtExpiracao)}</Text>
                  </>
                )}
                {pago && boleto?.dtCredito && (
                  <>
                    <Text color="gray.600" fontSize="xs" mt={2}>Pago em</Text>
                    <Text fontSize="sm">{formatDateWithNoHours(boleto.dtCredito)}</Text>
                  </>
                )}
                {boleto?.linhaDigitavel && !cancelado && (
                  <Text color="gray.600" fontSize="xs" mt={2} wordBreak="break-all">
                    {boleto.linhaDigitavel}
                  </Text>
                )}
              </Box>
            </Flex>
            <Text fontSize="sm" color="gray.600">
              {cancelado
                ? "Este boleto foi cancelado. Para cobrar a parcela, gere um novo boleto."
                : pago
                  ? "Este boleto já foi pago. Nenhuma ação disponível."
                  : "O que deseja fazer com este boleto?"}
            </Text>
          </Flex>
        </ModalBody>
        <ModalFooter gap={3} borderTopWidth="1px" borderTopColor="gray.200" pt={4}>
          {encerrado ? (
            <Button variant="outline" fontFamily="Lexend Deca" onClick={onClose}>
              Fechar
            </Button>
          ) : (
            <>
              <Button
                variant="outline"
                fontFamily="Lexend Deca"
                isDisabled={!boleto?.codigoCobranca || isCancelando}
                onClick={confirmacao.onOpen}
              >
                Cancelar boleto
              </Button>
              <Tooltip
                label="A bancarizadora ainda não devolveu o link de impressão deste boleto."
                isDisabled={Boolean(boleto?.urlImpressao)}
                placement="top"
              >
                <Button
                  {...tagButtonStyle("blue")}
                  fontFamily="Lexend Deca"
                  isDisabled={!boleto?.urlImpressao}
                  onClick={handleDownload}
                >
                  Download
                </Button>
              </Tooltip>
            </>
          )}
        </ModalFooter>
      </ModalContent>

      <ConfirmDialog
        isOpen={confirmacao.isOpen}
        onClose={confirmacao.onClose}
        onConfirm={handleConfirmarCancelamento}
        titulo="Cancelar boleto"
        descricao={
          <>
            O boleto{boleto?.numeroBoleto ? ` ${boleto.numeroBoleto}` : ""} será cancelado na
            bancarizadora. Essa ação não pode ser desfeita; para cobrar a parcela de novo será
            preciso gerar outro boleto.
          </>
        }
        confirmLabel="Cancelar boleto"
        isLoading={isCancelando}
      />
    </Modal>
  );
}
